import {
  ChangeEvent,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { t, Trans } from '@lingui/macro';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Message from '@decisiv/ui-components/lib/components/Message';
import { H3, H4, P } from '@decisiv/ui-components/lib/components/Typography';

import Table from '@decisiv/iconix/lib/components/Table';
import Trash from '@decisiv/iconix/lib/components/Trash';
import Upload from '@decisiv/iconix/lib/components/Upload';

import { eventStopPropagation } from 'utils/eventStopPropagation';

import {
  stepOneSubmitRef,
  useBulkCreateDepotContext,
} from '../context/BulkCreateDepotContext';
import { useCreateDepotBulkImportMutation } from '../hooks/useCreateDepotBulkImportMutation';
import {
  downloadDepotTemplate,
  validateDepotCsv,
  MAX_FILE_BYTES,
} from './csvTemplate';
import { HiddenFileInput, Row, FileCard, FileErrorRow } from './styles';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

const StepOne = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    savedState,
    setSavedState,
    setStepOneContinueButtonEnabled,
    setStepOneContinueButtonLoading,
  } = useBulkCreateDepotContext();

  const [uploadError, setUploadError] = useState<string | null>(null);
  // Keep the rejected file to display in the card UI.
  const [rejectedFile, setRejectedFile] = useState<File | null>(null);

  const [createDepotBulkImport] = useCreateDepotBulkImportMutation();

  const selectedFile = savedState.file;
  const displayedFile = selectedFile || rejectedFile;

  const handleOnUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setRejectedFile(null);
    setUploadError(null);
    setSavedState((prev) => ({ ...prev, file: undefined }));
    setStepOneContinueButtonEnabled(false);
  }, [setSavedState, setStepOneContinueButtonEnabled]);

  const handleOnFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const file = input.files?.[0];
      // Clear the value so re-selecting the same file fires change again.
      input.value = '';
      if (!file) return;

      setUploadError(null);
      setRejectedFile(null);

      let validationError: string | null;
      try {
        validationError = await validateDepotCsv(file);
      } catch {
        setUploadError(t`We couldn't read that file. Please try again.`);
        setRejectedFile(file);
        setSavedState((prev) => ({ ...prev, file: undefined }));
        setStepOneContinueButtonEnabled(false);
        return;
      }

      if (validationError) {
        setUploadError(validationError);
        setRejectedFile(file);
        setSavedState((prev) => ({ ...prev, file: undefined }));
        setStepOneContinueButtonEnabled(false);
        return;
      }

      setSavedState((prev) => ({ ...prev, file }));
      setStepOneContinueButtonEnabled(true);
    },
    [setSavedState, setStepOneContinueButtonEnabled],
  );

  const handleSubmit = useCallback(async () => {
    // Defensive: Continue is disabled until a file is selected.
    /* istanbul ignore next -- @preserve */
    if (!selectedFile) return;

    setUploadError(null);
    setStepOneContinueButtonLoading(true);

    try {
      const { data } = await createDepotBulkImport({
        variables: { originalFile: selectedFile },
      });

      const importId = data?.createDepotBulkImport?.depotBulkImport?.id;

      if (!importId) {
        setUploadError(
          t`We were unable to start your upload. Please try again.`,
        );
        return;
      }

      // Setting the import id moves the wizard into its intermediate
      // (processing) state; the Summary step renders once polling resolves.
      setSavedState((prev) => ({ ...prev, importId }));
    } catch {
      setUploadError(t`We were unable to start your upload. Please try again.`);
    } finally {
      setStepOneContinueButtonLoading(false);
    }
  }, [
    createDepotBulkImport,
    selectedFile,
    setSavedState,
    setStepOneContinueButtonLoading,
  ]);

  useImperativeHandle(stepOneSubmitRef, () => ({ submit: handleSubmit }), [
    handleSubmit,
  ]);

  const hasFileSizeError = !!rejectedFile && rejectedFile.size > MAX_FILE_BYTES;

  return (
    <Flex flexDirection="column">
      <H3>
        <Trans>List of Depots</Trans>
      </H3>
      <P size="small" shade={1} marginTop={0.5}>
        <Trans>Create multiple depots at once by uploading a CSV file</Trans>
      </P>

      <H4 marginTop={2}>
        <Trans>Upload File</Trans>
      </H4>

      {!displayedFile && (
        <>
          <P size="small" shade={1} marginTop={0.5}>
            <Trans>Upload a CSV file to create multiple depots</Trans>
          </P>

          <Row>
            <Button
              icon={Upload}
              text={t`Upload CSV File`}
              size="small"
              kind="secondary"
              type="button"
              onClick={handleOnUploadClick}
            />
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleOnFileChange}
              onClick={eventStopPropagation}
            />
            <Button
              icon={Table}
              text={t`Download CSV Template`}
              size="small"
              type="button"
              variant="ghost"
              onClick={downloadDepotTemplate}
            />
          </Row>

          <Flex alignItems="center" marginTop={0.5}>
            <Message intent="help" size="small">
              <Trans>
                Max file size 2 MB and/or 10,000 Depots. One depot per row
                required.
              </Trans>
            </Message>
          </Flex>
        </>
      )}

      {displayedFile && (
        <>
          <FileCard hasError={!!uploadError}>
            <Flex flexDirection="column" flex={1}>
              <P size="small" weight="semibold">
                {displayedFile.name}
              </P>
              <P
                size="small"
                shade={hasFileSizeError ? undefined : 1}
                color={hasFileSizeError ? 'danger' : undefined}
              >
                {formatFileSize(displayedFile.size)}
              </P>
            </Flex>
            <Button
              icon={Trash}
              size="small"
              kind="secondary"
              variant="ghost"
              onClick={handleRemoveFile}
              aria-label={t`Remove file`}
            />
          </FileCard>

          {uploadError && (
            <FileErrorRow>
              <Message intent="warning" size="small">
                {uploadError}
              </Message>
            </FileErrorRow>
          )}

          {/* Hidden input still needed for re-upload */}
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={handleOnFileChange}
            onClick={eventStopPropagation}
          />
        </>
      )}
    </Flex>
  );
};

export default StepOne;
